import { useEffect, useMemo, useState } from 'react';
import { resetSubscriberPassword, type SupportPasswordReset } from '../../lib/subscribers-api';
import type { PortalZone } from '../../lib/zones';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../lib/reauth';
import { InstantTime } from '../../lib/time-format';

/**
 * SupportPasswordModal (ADR-061) — staff-issued one-time support password.
 *
 * Two phases in one dialog:
 *   1. confirm — explain the action; primary "Generate password"
 *   2. done    — show the generated password ONCE, with NATO phonetics for
 *                reading over a support call, copy-to-clipboard, and the
 *                60-minute expiry warning.
 *
 * The password is never re-fetchable (the server stores only an expiry row, no
 * plaintext). If the staff member loses it before the customer sets their own,
 * they re-issue. The customer sets their own password on first sign-in
 * (FORCE_CHANGE_PASSWORD); no MFA on that first use.
 */

/** NATO phonetic for the caps group. Ambiguous O/I/S/Z are excluded by the
 *  generator, but the full map is here for safety. */
const NATO: Record<string, string> = {
  A: 'Alpha', B: 'Bravo', C: 'Charlie', D: 'Delta', E: 'Echo', F: 'Foxtrot',
  G: 'Golf', H: 'Hotel', I: 'India', J: 'Juliett', K: 'Kilo', L: 'Lima',
  M: 'Mike', N: 'November', O: 'Oscar', P: 'Papa', Q: 'Quebec', R: 'Romeo',
  S: 'Sierra', T: 'Tango', U: 'Uniform', V: 'Victor', W: 'Whiskey', X: 'X-ray',
  Y: 'Yankee', Z: 'Zulu',
};

function spellChar(ch: string): string {
  if (/[A-Z]/.test(ch)) return `${ch} — ${NATO[ch] ?? ch}`;
  if (/[a-z]/.test(ch)) return `${ch} — lowercase ${NATO[ch.toUpperCase()] ?? ch}`;
  return ch; // digit
}

export function SupportPasswordModal({
  subscriber,
  onClose,
  reset = resetSubscriberPassword,
}: {
  /**
   * `zone` is the SUBSCRIBER'S zone (ADR-122 Pattern 1), passed straight through to `reset`.
   *
   * ⚠ IT IS OPTIONAL BECAUSE THE STAFF CALLER HAS NO ZONE AND MUST NOT ACQUIRE ONE. This same
   * dialog serves the Staff detail modal, which passes `resetStaffPassword` against the GLOBAL
   * Staff table — platform-wide under ADR-120 Amendment 2, and proven absent from us-east-1.
   * That caller simply omits `zone`, and its reset function ignores the argument.
   */
  subscriber: { cognitoSub: string; email: string; zone?: PortalZone };
  onClose: () => void;
  /** The reset call to make. Defaults to the subscriber reset; the Staff detail
   *  modal passes resetStaffPassword to reuse this same dialog (batch Stage 3).
   *  Both return the identical SupportPasswordReset shape. */
  reset?: (cognitoSub: string, zone?: PortalZone) => Promise<SupportPasswordReset>;
}) {
  const toast = useToast();
  const reauth = useReAuth();
  const [phase, setPhase] = useState<'confirm' | 'done'>('confirm');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SupportPasswordReset | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [loading, onClose]);

  const groups = useMemo(() => (result ? result.password.split('-') : []), [result]);

  async function generate() {
    setLoading(true);
    try {
      // ADR-063: hard-block on a stale session — withReauth proactively gates
      // (and re-auths + retries on a server stale_auth 401) so the operator
      // re-runs MFA in place rather than seeing a raw 401 or being logged out.
      const res = await reauth.withReauth(() => reset(subscriber.cognitoSub, subscriber.zone));
      setResult(res);
      setPhase('done');
    } catch (err) {
      // Operator cancelled the re-auth modal — abort silently, no error toast.
      if (err instanceof ReauthCancelledError) return;
      // Keep the raw error for debugging only — never surface it to the operator.
      console.error('SupportPasswordModal: reset-password failed', err);
      toast.push({ variant: 'error', ...toastFromError(err, 'Could not generate password') });
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.push({
        variant: 'error',
        title: 'Could not copy',
        detail: 'Select the password and copy it manually.',
      });
    }
  }

  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div
        className="portal-modal portal-action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-pw-title"
      >
        <button
          type="button"
          className="portal-modal-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close dialog"
        >
          ×
        </button>

        {phase === 'confirm' && (
          <>
            <h2 id="support-pw-title">Reset password</h2>
            <div className="portal-action-modal-body">
              <p>
                Issue a one-time support password for <strong>{subscriber.email}</strong>?
              </p>
              <ul>
                <li>Read it to the customer over the phone, then have them sign in.</li>
                <li>They set their own password on first sign-in — no code is needed.</li>
                <li>
                  <strong>It expires in 60 minutes.</strong> After that, issue a new one.
                </li>
              </ul>
            </div>
            <div className="portal-modal-actions">
              <button
                type="button"
                className="portal-link-button-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="button" className="portal-button-warning" onClick={() => void generate()} disabled={loading}>
                {loading ? 'Generating…' : 'Generate password'}
              </button>
            </div>
          </>
        )}

        {phase === 'done' && result && (
          <>
            <h2 id="support-pw-title">Read this to the customer</h2>
            <div className="portal-action-modal-body">
              <p className="portal-support-pw" aria-label={`Support password ${result.password}`}>
                {result.password}
              </p>
              <button type="button" className="portal-link-button" onClick={() => void copy()}>
                {copied ? 'Copied ✓' : 'Copy to clipboard'}
              </button>

              <p className="portal-muted" style={{ marginTop: '0.75rem' }}>
                Spell it out — capitals first, then the numbers, then lowercase:
              </p>
              <ol className="portal-support-pw-spell">
                {groups.map((g, gi) => (
                  <li key={gi}>
                    <span className="portal-support-pw-group-label">
                      {gi === 0 ? 'Capitals' : gi === 1 ? 'Numbers' : 'Lowercase'}:
                    </span>{' '}
                    {[...g].map((ch, ci) => (
                      <span key={ci} className="portal-support-pw-char">
                        {spellChar(ch)}
                        {ci < g.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </li>
                ))}
              </ol>

              <div className="portal-warning" role="alert">
                This password expires in {result.expiresInMinutes} minutes
                {' '}(by <InstantTime iso={result.expiresAt * 1000} mode="time" />). It is shown once —
                it cannot be retrieved again.
              </div>
            </div>
            <div className="portal-modal-actions">
              <button type="button" className="portal-btn" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
